import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { fetchMyRbacData, ROLE_KEYS, MODULE_KEYS, PERM_KEYS, ROLE_TICKET_STATUSES } from '../services/rbacService';

const RBACContext = createContext({});

export const useRBAC = () => {
  const ctx = useContext(RBACContext);
  if (!ctx) throw new Error('useRBAC must be used within RBACProvider');
  return ctx;
};

export const RBACProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [rbacRoles, setRbacRoles] = useState([]);
  const [rbacRoleKeys, setRbacRoleKeys] = useState([]);
  const [rbacPermissions, setRbacPermissions] = useState({});
  const [rbacEmployeeId, setRbacEmployeeId] = useState(null);
  const [rbacLoading, setRbacLoading] = useState(true);

  const loadRbacData = useCallback(async () => {
    if (!user?.id) {
      setRbacRoles([]);
      setRbacRoleKeys([]);
      setRbacPermissions({});
      setRbacEmployeeId(null);
      setRbacLoading(false);
      return;
    }
    setRbacLoading(true);
    try {
      const data = await fetchMyRbacData(user?.id);
      setRbacRoles(data?.roles || []);
      setRbacRoleKeys(data?.roleKeys || []);
      setRbacPermissions(data?.permissions || {});
      setRbacEmployeeId(data?.employeeId || null);
    } catch (err) {
      console.warn('[RBAC] Load error:', err?.message);
    } finally {
      setRbacLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadRbacData();
  }, [loadRbacData]);

  // ---- Role checks ----
  const isSuperAdmin = rbacRoleKeys?.includes(ROLE_KEYS?.SUPER_ADMIN);
  const isRbacAdmin = rbacRoleKeys?.includes(ROLE_KEYS?.ADMIN) || isSuperAdmin;
  const isSalesUser = rbacRoleKeys?.includes(ROLE_KEYS?.SALES_USER);
  const isSalesManager = rbacRoleKeys?.includes(ROLE_KEYS?.SALES_MANAGER);
  const isProjectManager = rbacRoleKeys?.includes(ROLE_KEYS?.PROJECT_MANAGER);
  const isApprover = rbacRoleKeys?.includes(ROLE_KEYS?.APPROVER);
  const isFinance = rbacRoleKeys?.includes(ROLE_KEYS?.FINANCE);
  const isHrMasterData = rbacRoleKeys?.includes(ROLE_KEYS?.HR_MASTER_DATA);
  const isEmployee = rbacRoleKeys?.includes(ROLE_KEYS?.EMPLOYEE);
  const isAuditor = rbacRoleKeys?.includes(ROLE_KEYS?.AUDITOR);

  // Also check legacy role from user_profiles for backward compat
  const legacyRole = userProfile?.role;
  const isLegacyAdmin = legacyRole === 'admin';

  // Combined admin check
  const isAdmin = isRbacAdmin || isLegacyAdmin;

  // ---- Permission check ----
  const hasPermission = useCallback((module, permission) => {
    if (isAdmin) return true; // Admin has everything
    return rbacPermissions?.[module]?.[permission] === true;
  }, [isAdmin, rbacPermissions]);

  // ---- Module access check ----
  const canAccessModule = useCallback((module) => {
    if (isAdmin) return true;
    // Grant access if user has view, view_all, or view_own permission for the module
    return (
      rbacPermissions?.[module]?.[PERM_KEYS?.VIEW] === true ||
      rbacPermissions?.[module]?.[PERM_KEYS?.VIEW_ALL] === true ||
      rbacPermissions?.[module]?.[PERM_KEYS?.VIEW_OWN] === true
    );
  }, [isAdmin, rbacPermissions]);

  // ---- View scope helpers ----
  // Returns true if user has view_all permission (or is admin)
  const canViewAll = useCallback((module) => {
    if (isAdmin) return true;
    return rbacPermissions?.[module]?.[PERM_KEYS?.VIEW_ALL] === true;
  }, [isAdmin, rbacPermissions]);

  // Returns true if user has ONLY view_own (no view_all, no admin)
  const canViewOwn = useCallback((module) => {
    if (isAdmin) return false; // admin sees everything, no own-only restriction
    if (rbacPermissions?.[module]?.[PERM_KEYS?.VIEW_ALL] === true) return false; // view_all overrides view_own
    return rbacPermissions?.[module]?.[PERM_KEYS?.VIEW_OWN] === true;
  }, [isAdmin, rbacPermissions]);

  // Returns true if user has view_team permission (and not view_all, not admin)
  const canViewTeam = useCallback((module) => {
    if (isAdmin) return false;
    if (rbacPermissions?.[module]?.[PERM_KEYS?.VIEW_ALL] === true) return false;
    return rbacPermissions?.[module]?.[PERM_KEYS?.VIEW_TEAM] === true;
  }, [isAdmin, rbacPermissions]);

  // ---- Ticket status visibility ----
  const getVisibleTicketStatuses = useCallback(() => {
    if (isAdmin) return null; // null = all
    // Check each role
    for (const roleKey of rbacRoleKeys) {
      const statuses = ROLE_TICKET_STATUSES?.[roleKey];
      if (statuses !== undefined) return statuses;
    }
    return null;
  }, [isAdmin, rbacRoleKeys]);

  // ---- Sidebar items by role ----
  const getSidebarItems = useCallback(() => {
    const items = [];

    // ---- Main section ----
    const mainItems = [];
    if (canAccessModule(MODULE_KEYS?.SERVICE_CALCULATOR)) {
      mainItems?.push({ id: 'service-calculator', label: 'Service Calculator', path: '/service-calculator', icon: 'Calculator' });
    }
    if (canAccessModule(MODULE_KEYS?.ANALYTICS_DASHBOARD)) {
      mainItems?.push({ id: 'analytics-dashboard', label: 'Analytics Dashboard', path: '/analytics-dashboard', icon: 'BarChart3' });
    }
    mainItems?.push({ id: 'executive-snapshot', label: 'Executive Snapshot', path: '/executive-snapshot', icon: 'LayoutDashboard', tooltip: 'Executive Snapshot' });
    if (mainItems?.length > 0) {
      items?.push({ id: 'main-sep', label: 'Main', path: null, icon: null, isSectionHeader: true });
      items?.push(...mainItems);
    }

    // ---- Incentive Tickets section ----
    const incentiveItems = [];
    if (canAccessModule(MODULE_KEYS?.INCENTIVE_TICKETS)) {
      incentiveItems?.push({ id: 'incentive-tickets', label: 'Project Incentive Tickets', path: '/incentive-tickets', icon: 'Ticket' });
    }
    if (canAccessModule(MODULE_KEYS?.TEAM_ASSIGNMENT)) {
      incentiveItems?.push({ id: 'pending-assignment', label: 'Pending Team Assignment', path: '/incentive-tickets/pending-assignment', icon: 'UserPlus' });
    }
    if (canAccessModule(MODULE_KEYS?.PENDING_APPROVAL)) {
      incentiveItems?.push({ id: 'pending-approval', label: 'Pending Approval', path: '/incentive-tickets/pending-approval', icon: 'ClipboardCheck' });
      if (isAdmin) {
        incentiveItems?.push({ id: 'approved', label: 'Approved Tickets', path: '/incentive-tickets/approved', icon: 'CheckCircle' });
        incentiveItems?.push({ id: 'rejected', label: 'Rejected / On Hold', path: '/incentive-tickets/rejected', icon: 'XCircle' });
      }
    }
    if (canAccessModule(MODULE_KEYS?.FINANCE_PAYMENTS)) {
      incentiveItems?.push({ id: 'finance-payments', label: 'Finance Payments', path: '/incentive-tickets/finance-report', icon: 'Banknote' });
      if (isAdmin || isFinance) {
        incentiveItems?.push({ id: 'ready-for-payment', label: 'Ready for Payment', path: '/incentive-tickets/ready-for-payment', icon: 'Clock' });
        incentiveItems?.push({ id: 'paid', label: 'Paid Payments', path: '/incentive-tickets/paid', icon: 'CheckCircle' });
      }
    }
    if (canAccessModule(MODULE_KEYS?.EMPLOYEE_INCENTIVE_REPORTS)) {
      incentiveItems?.push({ id: 'employee-incentive-reports', label: 'Employee Incentive Reports', path: '/incentive-tickets/employee-report', icon: 'BarChart2' });
    }
    if (incentiveItems?.length > 0) {
      items?.push({ id: 'incentive-sep', label: 'Incentive Tickets', path: null, icon: null, isSectionHeader: true });
      items?.push(...incentiveItems);
    }

    // ---- Notification History (always visible to authenticated users) ----
    items?.push({ id: 'notification-history', label: 'Notification History', path: '/notification-history', icon: 'BellRing', tooltip: 'Notification History' });

    // ---- Tender Management section ----
    if (canAccessModule(MODULE_KEYS?.TENDER_MANAGEMENT)) {
      items?.push({ id: 'tender-separator', label: 'Tender Management', path: null, icon: 'FileSearch', isSectionHeader: true });
      items?.push({ id: 'tender-analyzer', label: 'AI Tender Analyzer', path: '/tender-analyzer', icon: 'Brain' });
      items?.push({ id: 'tender-uploaded', label: 'Uploaded Tenders', path: '/tender-uploaded', icon: 'FolderOpen' });
      items?.push({ id: 'tender-compliance', label: 'Compliance Reports', path: '/tender-compliance', icon: 'ShieldCheck' });
      items?.push({ id: 'tender-tasks', label: 'Generated Tasks', path: '/tender-tasks', icon: 'ListTodo' });
      items?.push({ id: 'tender-dashboard', label: 'Tender Dashboard', path: '/tender-dashboard', icon: 'LayoutDashboard' });
    }

    // ---- Administration section ----
    const adminItems = [];
    if (canAccessModule(MODULE_KEYS?.MASTER_DATA)) {
      adminItems?.push({ id: 'master-data', label: 'Master Data', path: '/master-data-management', icon: 'Database' });
    }
    if (canAccessModule(MODULE_KEYS?.ROLES_PERMISSIONS)) {
      adminItems?.push({ id: 'roles-permissions', label: 'Roles & Permissions', path: '/role-permission-management', icon: 'ShieldCheck' });
    }
    if (adminItems?.length > 0) {
      items?.push({ id: 'admin-sep', label: 'Administration', path: null, icon: null, isSectionHeader: true });
      items?.push(...adminItems);
    }

    // ---- Account section (always visible) ----
    items?.push({ id: 'account-sep', label: 'Account', path: null, icon: null, isSectionHeader: true });
    items?.push({ id: 'settings', label: 'Settings', path: '/account-management', icon: 'Settings' });

    return items;
  }, [isAdmin, isFinance, canAccessModule]);

  const value = {
    // State
    rbacRoles,
    rbacRoleKeys,
    rbacPermissions,
    rbacEmployeeId,
    rbacLoading,
    // Role flags
    isSuperAdmin,
    isRbacAdmin,
    isAdmin,
    isSalesUser,
    isSalesManager,
    isProjectManager,
    isApprover,
    isFinance,
    isHrMasterData,
    isEmployee,
    isAuditor,
    // Methods
    hasPermission,
    canAccessModule,
    canViewAll,
    canViewOwn,
    canViewTeam,
    getVisibleTicketStatuses,
    getSidebarItems,
    reloadRbac: loadRbacData,
    // Constants
    ROLE_KEYS,
    MODULE_KEYS,
    PERM_KEYS,
  };

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
};

export default RBACContext;
